import { useAppState } from "../../context";
import { UserType } from "../../types";
import { useForm } from "react-hook-form";
import { fetchJson } from "../../utils";
import { useState } from "react";
import { Link } from "react-router";

type FormData = {
    email: string;
    firstName: string;
    lastName: string;
    company?: string;
    country: string;
    streetAddress: string;
    apartment?: string;
    city: string;
    county?: string;
    postcode: string;
    phone: string;
    orderNotes?: string;
};

export const AccountPersonalInformation = () => {
    const [user, setUser] = useAppState<UserType>('user');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
        defaultValues: {
            email: user.email,
            firstName: user.profile?.firstName ?? '',
            lastName: user.profile?.lastName ?? '',
            company: user.profile?.company ?? '',
            country: user.profile?.country ?? '',
            streetAddress: user.profile?.streetAddress ?? '',
            apartment: user.profile?.apartment ?? '',
            city: user.profile?.city ?? '',
            county: user.profile?.county ?? '',
            postcode: user.profile?.postcode ?? '',
            phone: user.profile?.phone ?? '',
            orderNotes: user.profile?.orderNotes ?? '',
        }
    });

    const onSubmit = async (data: FormData) => {
        try {
            setSaving(true);
            setError(null);
            const { json } = await fetchJson<{success: boolean; message: string}>('/api/profile', {
                method: 'POST',
                body: JSON.stringify(data)
            });
            
            if (json.success) {
                setUser({
                    ...user,
                    email: data.email,
                    profile: {
                        ...data
                    }
                });
            } else {
                setError(json.message);
            }
        } catch (err) {
            setError('Failed to save profile information');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <h1 className="section__title">
                Your Personal Information
            </h1>

            <div className="persinfo">
                <div className="persinfo__form form">
                    {error && <div className="form__alert form__alert_global">{error}</div>}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={`form__row ${errors.email && 'is-error'}`}>
                            <label className="form__label">E-mail</label>
                            <div className="form__input-holder">
                                <input 
                                    type="email" 
                                    className="form__input"
                                    {...register('email', { 
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address'
                                        }
                                    })} 
                                />
                            </div>
                            {errors.email && <div className="form__alert">{errors.email.message}</div>}
                        </div>

                        <div className={`form__row ${errors.firstName && 'is-error'}`}>
                            <label className="form__label">First name</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('firstName', { required: 'First name is required' })} 
                                />
                            </div>
                            {errors.firstName && <div className="form__alert">{errors.firstName.message}</div>}
                        </div>

                        <div className={`form__row ${errors.lastName && 'is-error'}`}>
                            <label className="form__label">Last name</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('lastName', { required: 'Last name is required' })} 
                                />
                            </div>
                            {errors.lastName && <div className="form__alert">{errors.lastName.message}</div>}
                        </div>

                        <div className="form__row">
                            <label className="form__label">Company name (optional)</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('company')} 
                                />
                            </div>
                        </div>

                        <div className={`form__row ${errors.country && 'is-error'}`}>
                            <label className="form__label">Country / Region</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('country', { required: 'Country is required' })} 
                                />
                            </div>
                            {errors.country && <div className="form__alert">{errors.country.message}</div>}
                        </div>

                        <div className={`form__row ${errors.streetAddress && 'is-error'}`}>
                            <label className="form__label">Street address</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    placeholder="House number and street name"
                                    {...register('streetAddress', { required: 'Street address is required' })} 
                                />
                            </div>
                            {errors.streetAddress && <div className="form__alert">{errors.streetAddress.message}</div>}
                        </div>

                        <div className="form__row">
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    placeholder="Apartment, suite, unit, etc. (optional)"
                                    {...register('apartment')} 
                                />
                            </div>
                        </div>

                        <div className={`form__row ${errors.city && 'is-error'}`}>
                            <label className="form__label">Town / City</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('city', { required: 'City is required' })} 
                                />
                            </div>
                            {errors.city && <div className="form__alert">{errors.city.message}</div>}
                        </div>

                        <div className="form__row">
                            <label className="form__label">County (optional)</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('county')} 
                                />
                            </div>
                        </div>

                        <div className={`form__row ${errors.postcode && 'is-error'}`}>
                            <label className="form__label">Postcode</label>
                            <div className="form__input-holder">
                                <input 
                                    type="text" 
                                    className="form__input"
                                    {...register('postcode', { required: 'Postcode is required' })} 
                                />
                            </div>
                            {errors.postcode && <div className="form__alert">{errors.postcode.message}</div>}
                        </div>

                        <div className={`form__row ${errors.phone && 'is-error'}`}>
                            <label className="form__label">Phone</label>
                            <div className="form__input-holder">
                                <input 
                                    type="tel" 
                                    className="form__input"
                                    {...register('phone', { 
                                        required: 'Phone is required',
                                        pattern: {
                                            value: /^[0-9+\-\s()]*$/,
                                            message: 'Invalid phone number'
                                        }
                                    })} 
                                />
                            </div>
                            {errors.phone && <div className="form__alert">{errors.phone.message}</div>}
                        </div>

                        <div className="form__row">
                            <label className="form__label">Order notes (optional)</label>
                            <div className="form__input-holder">
                                <textarea 
                                    className="form__input form__textarea js_textarea"
                                    {...register('orderNotes')}
                                />
                            </div>
                        </div>

                        <div className="form__row form__row_extragap form__note">
                            <Link to="/account/change-password" className="btn-link">Change password</Link>
                        </div>

                        <div className="form__row form__row_smallgap form__btns">
                            <button 
                                className="btn btn_cta btn_lg btn_extrapad" 
                                type="submit"
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AccountPersonalInformation; 